/**
 * Targeting Rule Builder
 *
 * Main component for building and editing targeting rules
 */

import { useState, useCallback, useEffect } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Button,
  Text,
  Banner,
  Spinner,
} from "@shopify/polaris";
import type { TargetingRuleV1, Condition, LogicalCondition, ValidationError } from "./types";
import { GroupEditor } from "./GroupEditor";
import { PreviewPanel } from "./PreviewPanel";
import { hasActionableTargetingRules, validateTargetingRule } from "./validation";
import { TARGETING_PRESETS } from "./targetingPresets";

interface RuleBuilderProps {
  rule: TargetingRuleV1 | null;
  onChange: (rule: TargetingRuleV1 | null) => void;
  onValidate?: (rule: TargetingRuleV1) => Promise<{ valid: boolean; errors: ValidationError[] }>;
  showPreview?: boolean;
}

const DEFAULT_LEAF: Condition = {
  type: "customer_total_spent",
  operator: "gt",
  value: 100,
};

function isLogicalGroupCondition(
  condition: Condition | undefined
): condition is LogicalCondition {
  return (
    condition != null &&
    (condition.type === "and" || condition.type === "or" || condition.type === "not")
  );
}

export function RuleBuilder({
  rule,
  onChange,
  onValidate = validateTargetingRule,
  showPreview = true,
}: RuleBuilderProps) {
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [showPreviewPanel, setShowPreviewPanel] = useState(false);

  const actionable = rule != null && hasActionableTargetingRules(rule);

  /**
   * Validate rule on change (debounced); skip when no real targeting (empty / everyone).
   */
  useEffect(() => {
    if (!rule || !actionable) {
      setErrors([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsValidating(true);
      try {
        const result = await onValidate(rule);
        setErrors(result.valid ? [] : result.errors);
      } catch (error) {
        console.error("Validation failed:", error);
        setErrors([
          {
            path: "rule",
            message: "Validation failed. Please check your rule.",
            code: "VALIDATION_ERROR",
          },
        ]);
      } finally {
        setIsValidating(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [rule, onValidate, actionable]);

  const handleRuleChange = useCallback(
    (newRootCondition: Condition) => {
      if (!rule) return;

      const updatedRule: TargetingRuleV1 = {
        ...rule,
        rule: newRootCondition,
      };

      onChange(updatedRule);
    },
    [rule, onChange]
  );

  const handleAddCondition = useCallback(() => {
    if (!rule) {
      onChange({
        version: "1",
        rule: {
          type: "and",
          conditions: [DEFAULT_LEAF],
        },
      });
      return;
    }

    const root = rule.rule;
    if (!isLogicalGroupCondition(root)) return;

    handleRuleChange({
      ...root,
      conditions: [...root.conditions, DEFAULT_LEAF],
    });
  }, [rule, onChange, handleRuleChange]);

  const handleAddGroup = useCallback(() => {
    const newGroup: LogicalCondition = {
      type: "and",
      conditions: [],
    };

    if (!rule) {
      onChange({
        version: "1",
        rule: {
          type: "and",
          conditions: [newGroup],
        },
      });
      return;
    }

    const root = rule.rule;
    if (!isLogicalGroupCondition(root)) return;

    handleRuleChange({
      ...root,
      conditions: [...root.conditions, newGroup],
    });
  }, [rule, onChange, handleRuleChange]);

  const handleClearTargeting = useCallback(() => {
    onChange(null);
    setErrors([]);
  }, [onChange]);

  if (!rule || !actionable) {
    return (
      <Card>
        <BlockStack gap="400">
          <BlockStack gap="200">
            <Text variant="headingSm" as="h2">
              No targeting rules
            </Text>
            <Text tone="subdued" as="p">
              This campaign will be shown to all visitors.
            </Text>

            <InlineStack gap="200" wrap>
              <Button onClick={handleAddCondition}>Add condition</Button>
              <Button variant="plain" onClick={handleAddGroup}>
                Add group
              </Button>
            </InlineStack>
          </BlockStack>

          <BlockStack gap="200">
            <Text variant="bodySm" fontWeight="semibold" as="p">
              Quick start
            </Text>
            <Text tone="subdued" variant="bodySm" as="p">
              Choose a preset to add rules you can edit, or use Add condition / Add group.
            </Text>
            <InlineStack gap="200" wrap blockAlign="center">
              {TARGETING_PRESETS.map((preset) => (
                <Button
                  key={preset.id}
                  size="slim"
                  variant="secondary"
                  onClick={() => onChange(preset.buildRule())}
                  accessibilityLabel={preset.helpText ?? preset.label}
                >
                  {preset.label}
                </Button>
              ))}
            </InlineStack>
          </BlockStack>
        </BlockStack>
      </Card>
    );
  }

  return (
    <BlockStack gap="400">
      <Banner tone="info">
        <Text as="p">
          Targeting is active. Only visitors matching these rules will see this campaign.
        </Text>
      </Banner>

      <InlineStack align="space-between" blockAlign="center" wrap>
        <InlineStack gap="200" blockAlign="center">
          {isValidating && <Spinner size="small" />}
          {showPreview && (
            <Button
              onClick={() => setShowPreviewPanel(!showPreviewPanel)}
              variant="plain"
            >
              {showPreviewPanel ? "Hide" : "Show"} preview
            </Button>
          )}
        </InlineStack>
        <Button onClick={handleClearTargeting} variant="plain" tone="critical">
          Clear all targeting
        </Button>
      </InlineStack>

      <Text variant="headingSm" as="h3">
        Conditions
      </Text>

      {isLogicalGroupCondition(rule.rule) ? (
        <GroupEditor
          group={rule.rule}
          onChange={handleRuleChange}
          errors={errors}
          path="rule"
          depth={0}
        />
      ) : (
        <Text as="p" tone="critical">
          This rule could not be loaded. Clear targeting and start again, or contact support.
        </Text>
      )}

      {showPreviewPanel && <PreviewPanel rule={rule} />}
    </BlockStack>
  );
}
